// record_repeat — the learner hears a model phrase, then records themselves
// repeating it. Presentational until Phase 2 (Whisper grading, CLAUDE.md §16):
// the recording is captured + playable client-side but not scored, and "viewed"
// (i.e. the learner engaged) is full credit.
//
// Recording uses MediaRecorder; if the browser/permission denies it, the
// activity still completes on mount so a kid is never blocked from continuing.

import { useEffect, useRef, useState, type ReactElement } from 'react';

import type { content } from '@fajrarabi/zod-schemas';

import { ArabicText, AudioButton } from './primitives';
import type { ActivityRendererProps } from './types';

type RecState = 'idle' | 'recording' | 'recorded' | 'unsupported';

export function RecordRepeat({
  data,
  ctx,
  cb,
}: ActivityRendererProps<content.RecordRepeatData>): ReactElement {
  const { t } = ctx;
  const [state, setState] = useState<RecState>('idle');
  const [clipUrl, setClipUrl] = useState<string | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);

  useEffect(() => {
    // Presentational: enable Continue immediately; recording is a bonus.
    cb.onViewed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Revoke the object URL when it changes / unmounts to avoid a leak.
  useEffect(() => {
    return () => {
      if (clipUrl) URL.revokeObjectURL(clipUrl);
    };
  }, [clipUrl]);

  async function startRecording(): Promise<void> {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setState('unsupported');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' });
        setClipUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return URL.createObjectURL(blob);
        });
        setState('recorded');
        stream.getTracks().forEach((tr) => tr.stop());
      };
      recorderRef.current = recorder;
      recorder.start();
      setState('recording');
    } catch {
      setState('unsupported');
    }
  }

  function stopRecording(): void {
    recorderRef.current?.stop();
  }

  function playClip(): void {
    if (!clipUrl) return;
    const audio = new Audio(clipUrl);
    void audio.play().catch(() => {
      /* non-fatal */
    });
  }

  return (
    <div className="mx-auto flex w-full max-w-sm flex-col items-center text-center">
      <p className="text-sm font-semibold uppercase tracking-wide text-ink-500">
        {t('content.record_repeat.say_this')}
      </p>
      <h2 className="mt-2 text-3xl font-bold text-burgundy-700">
        <ArabicText text={t(data.phraseKey)} ctx={ctx} />
      </h2>
      {data.glossKey ? <p className="mt-1 text-ink-500">{t(data.glossKey)}</p> : null}

      <div className="mt-5">
        <AudioButton src={data.modelAudio} ctx={ctx} label={t('content.common.play_audio')} />
      </div>

      <div className="mt-8 flex items-center gap-3">
        {state === 'recording' ? (
          <button
            type="button"
            onClick={stopRecording}
            className="inline-flex min-h-[56px] items-center gap-2 rounded-full bg-heart px-7 text-lg font-bold text-cream-50 shadow-sm transition-transform hover:scale-105 active:scale-95"
          >
            <span className="inline-block h-3 w-3 animate-pulse rounded-sm bg-cream-50" />
            {t('content.record_repeat.stop')}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => void startRecording()}
            className="inline-flex min-h-[56px] items-center gap-2 rounded-full bg-burgundy-500 px-7 text-lg font-bold text-cream-50 shadow-sm transition-transform hover:scale-105 active:scale-95"
          >
            🎙️{' '}
            {t(
              state === 'recorded' ? 'content.record_repeat.again' : 'content.record_repeat.start',
            )}
          </button>
        )}

        {state === 'recorded' && clipUrl ? (
          <button
            type="button"
            onClick={playClip}
            aria-label={t('content.record_repeat.playback')}
            className="grid h-14 w-14 place-items-center rounded-full bg-sun-300 text-2xl text-burgundy-700 shadow-sm transition-transform hover:scale-105 active:scale-95"
          >
            ▶️
          </button>
        ) : null}
      </div>

      {state === 'unsupported' ? (
        <p className="mt-4 text-sm text-ink-500">{t('content.record_repeat.unsupported')}</p>
      ) : null}
    </div>
  );
}
